/**
 * Shared client/API validation feedback for internal forms.
 */

export function getFirstFieldError(errors) {
  if (!errors || typeof errors !== 'object') return null;
  for (const value of Object.values(errors)) {
    if (Array.isArray(value)) {
      const msg = value.find(Boolean);
      if (msg) return String(msg);
    } else if (value != null && value !== '') {
      return String(value);
    }
  }
  return null;
}

/** Collect unique non-empty messages from a field-errors object. */
export function collectFieldErrorMessages(errors) {
  if (!errors || typeof errors !== 'object') return [];
  const msgs = [];
  Object.values(errors).forEach((value) => {
    if (Array.isArray(value)) {
      value.filter(Boolean).forEach((m) => msgs.push(String(m)));
    } else if (value != null && value !== '') {
      msgs.push(String(value));
    }
  });
  return [...new Set(msgs)];
}

/** Banner text listing every validation issue at once (Event / Task style). */
export function formatFieldErrorsMessage(errors, fallback = 'Please fix the highlighted fields.') {
  const msgs = collectFieldErrorMessages(errors);
  if (!msgs.length) return fallback;
  if (msgs.length === 1) return msgs[0];
  return `Please fix the following: ${msgs.join('. ')}.`;
}

/** Gate field helper text / error borders until after submit or confirm. */
export function createFieldErrorGate(showFieldErrors, validationErrors) {
  return {
    fieldHelper: (name) => (showFieldErrors && validationErrors[name] ? validationErrors[name] : undefined),
    fieldHasError: (name) => Boolean(showFieldErrors && validationErrors[name]),
  };
}

export function getFirstErrorMessage(data) {
  const errs = data?.errors;
  if (errs && typeof errs === 'object') {
    const first = Object.values(errs).flat().find(Boolean);
    if (first) return first;
  }
  const parts = [data?.error, data?.hint, data?.message].filter(Boolean);
  return parts.length ? parts.join(' ') : null;
}

/** Banner text for API validation responses — lists every issue, not just the first. */
export function formatApiErrorMessage(data, fallback = 'Please fix the highlighted fields.') {
  if (data?.errors && typeof data.errors === 'object') {
    return formatFieldErrorsMessage(data.errors, fallback);
  }
  return data?.message || data?.error || fallback;
}

export function applyApiValidationErrors(
  data,
  setValidationErrors,
  allowedFields = null,
  fieldMap = {},
) {
  const errs = data?.errors;
  if (!errs || typeof errs !== 'object') return;

  const next = {};
  Object.entries(errs).forEach(([key, msgs]) => {
    const formKey = fieldMap[key] || key;
    if (allowedFields && !allowedFields.has(formKey)) return;
    const msg = Array.isArray(msgs) ? msgs[0] : msgs;
    if (msg) next[formKey] = msg;
  });

  if (Object.keys(next).length) {
    setValidationErrors((prev) => ({ ...prev, ...next }));
  }
}

/**
 * After the user confirms an edit, close the dialog and show field + banner errors.
 */
export function rejectConfirmValidation({
  errors,
  setValidationErrors,
  setError,
  setConfirmUpdateDialogOpen,
  setPendingUpdateRequest,
  setShowFieldErrors,
}) {
  setValidationErrors(errors);
  setShowFieldErrors?.(true);
  if (setError) setError(formatFieldErrorsMessage(errors));
  setConfirmUpdateDialogOpen?.(false);
  setPendingUpdateRequest?.(null);
}
